import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, matchPath } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useChat } from '../../../app/providers/ChatContext';
import ChatFab from './ChatFab';
import FloatingChatPanel from './FloatingChatPanel';

const FloatingChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const lastCloseTimestampRef = useRef<number>(Date.now());
  const location = useLocation();
  const navigate = useNavigate();
  const { currentThread, selectHotel, state } = useChat();

  // Hide on the full chat page
  const isOnChatPage = location.pathname === '/chat';

  // Auto-detect hotel context from URL
  useEffect(() => {
    const match = matchPath('/hotels/:hotelId/*', location.pathname)
      || matchPath('/hotels/:hotelId', location.pathname);

    if (match?.params?.hotelId) {
      const hotelId = match.params.hotelId;
      // Only update if different from current selection
      if (hotelId !== state.selectedHotelId) {
        // We don't have the hotel name from the URL alone, so we set just the ID.
        // The chat will still work with just the hotelId; name is optional context.
        selectHotel(hotelId);
      }
    }
  }, [location.pathname, state.selectedHotelId, selectHotel]);

  // Track unread messages when widget is closed
  useEffect(() => {
    if (isOpen || !currentThread) {
      if (isOpen) setUnreadCount(0);
      return;
    }

    const newMessages = currentThread.messages.filter(
      (msg) =>
        msg.role === 'assistant' &&
        !msg.isStreaming &&
        msg.timestamp.getTime() > lastCloseTimestampRef.current
    );
    setUnreadCount(newMessages.length);
  }, [currentThread?.messages, isOpen, currentThread]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    setUnreadCount(0);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    lastCloseTimestampRef.current = Date.now();
  }, []);

  const handleExpandFull = useCallback(() => {
    setIsOpen(false);
    navigate('/chat');
  }, [navigate]);

  // Don't render on the /chat page
  if (isOnChatPage) return null;

  return (
    <>
      {!isOpen && (
        <ChatFab onClick={handleOpen} unreadCount={unreadCount} />
      )}
      <AnimatePresence>
        {isOpen && (
          <FloatingChatPanel
            onClose={handleClose}
            onExpandFull={handleExpandFull}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingChatWidget;
